import React, { useMemo, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SearchIcon, DownloadIcon, ArrowRightIcon, FilterIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SEO } from '../components/SEO';
import { CaseWebsiteLink } from '../components/CaseWebsiteLink';
import { buildBreadcrumbList, buildCollectionSchema } from '../lib/structuredData';
import { articles } from '../data/blogArticles';
import { blogPosts as allBlogPosts, getBlogImage } from '../data/allBlogPosts';
import { clientCases as realClientCases } from '../data/allClientCases';
type ItemType = 'blog' | 'seo' | 'case' | 'logo' | 'norden';
interface LibraryItem {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  link: string;
  type: ItemType;
  actionText: string;
  /** Alleen bij cases: live adres van het project, als dat er is. */
  website?: string;
  imageContain?: boolean;
}
// 1. Blogartikelen (uit data/allBlogPosts.ts, alle 20 posts)
const blogPosts: LibraryItem[] = allBlogPosts.map(post => ({
  id: `blog-${post.slug}`,
  title: post.title,
  category: post.category,
  description: post.excerpt,
  image: getBlogImage(post),
  link: `/blog/${post.slug}`,
  type: 'blog',
  actionText: 'Lees artikel'
}));
// 2. Client Cases (from data/allClientCases.ts, alle 20 cases in vaste volgorde)
const clientCases: LibraryItem[] = realClientCases.map(c => ({
  id: `case-${c.slug}`,
  title: c.clientName,
  category: c.sector,
  description: c.shortDescription,
  image: c.heroImage,
  link: `/cases/${c.slug}`,
  type: 'case',
  actionText: 'Lees het verhaal',
  website: c.website,
  imageContain: c.heroImageContain
}));
// 3. SEO Articles (from data/blogArticles.ts, includes all 64 articles)
const seoArticles: LibraryItem[] = articles.map(article => ({
  id: `seo-${article.slug}`,
  title: article.title,
  category: article.cluster,
  description: article.metaDescription,
  image: 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  link: `/seo/blog/${article.slug}`,
  type: 'seo',
  actionText: 'Lees artikel'
}));
// 4. Logo's & Branding
const NORDEN_PORTRAIT = 'https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png';
const logos: LibraryItem[] = [{
  id: 'logo-portret',
  title: 'Noord Digital Logo (Norden portret)',
  category: 'Branding',
  description: 'Het primaire Noord Digital logo: het portret van Norden in 300 dpi. Geschikt voor lichte achtergronden, social, presentaties en e-mailhandtekeningen.',
  image: NORDEN_PORTRAIT,
  link: NORDEN_PORTRAIT,
  type: 'logo',
  actionText: 'Download afbeelding'
}];
// 5. Norden Mascotte
const nordenAssets: LibraryItem[] = [{
  id: 'norden-portret',
  title: 'Norden Portret',
  category: 'Mascotte',
  description: "Hoge resolutie portret van Norden de ijsbeer. Perfect voor teampagina's, avatars en algemene communicatie.",
  image: NORDEN_PORTRAIT,
  link: NORDEN_PORTRAIT,
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-fullbody',
  title: 'Norden Full Body',
  category: 'Mascotte',
  description: 'Volledige weergave van Norden, gespiegeld. Ideaal voor presentaties, banners en grote visuele elementen.',
  image: 'https://cdn.magicpatterns.com/uploads/aj1Btym2B39xizjMwoi1Gy/Norden_full_body_gespiegeld.png',
  link: 'https://cdn.magicpatterns.com/uploads/aj1Btym2B39xizjMwoi1Gy/Norden_full_body_gespiegeld.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-kleurwaaier',
  title: 'Norden met kleurwaaier',
  category: 'Mascotte',
  description: 'Norden met kleurwaaier. Gebruikt in het werkproces voor de fase ontwerp en huisstijl.',
  image: 'https://cdn.magicpatterns.com/uploads/hr1JC9wRjcR2KJD3mjAfSG/Norden_met_kleur_waaier.png',
  link: 'https://cdn.magicpatterns.com/uploads/hr1JC9wRjcR2KJD3mjAfSG/Norden_met_kleur_waaier.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-tandwiel',
  title: 'Norden met tandwiel',
  category: 'Mascotte',
  description: 'Norden met tandwiel. Symboliseert techniek, optimalisatie en ontwikkeling van de website.',
  image: 'https://cdn.magicpatterns.com/uploads/k2oc6i7aAK288UUd8m2sTF/Norden_met_tandwiel.png',
  link: 'https://cdn.magicpatterns.com/uploads/k2oc6i7aAK288UUd8m2sTF/Norden_met_tandwiel.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-raket',
  title: 'Norden met raket',
  category: 'Mascotte',
  description: 'Norden met raket. Past bij lancering, livegang en groei. Goed voor launch-content en succes-momenten.',
  image: 'https://cdn.magicpatterns.com/uploads/1WqxCzJ38hFJxSHm98buvx/Norden_met_raket.png',
  link: 'https://cdn.magicpatterns.com/uploads/1WqxCzJ38hFJxSHm98buvx/Norden_met_raket.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-plant',
  title: 'Norden met plant',
  category: 'Mascotte',
  description: 'Norden met plant. Staat voor groei, onderhoud en de fase waarin de website blijft bloeien.',
  image: 'https://cdn.magicpatterns.com/uploads/3MuSnfaWEHYo1WZXKtBhAK/Norden_met_plant.png',
  link: 'https://cdn.magicpatterns.com/uploads/3MuSnfaWEHYo1WZXKtBhAK/Norden_met_plant.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-cookie',
  title: 'Norden eet een cookie',
  category: 'Mascotte',
  description: 'Norden die smikkelt van een koekje. Speciaal voor de cookie-banner en luchtige content over privacy.',
  image: 'https://cdn.magicpatterns.com/uploads/a7QwrfsRheFwXZaY2yk98u/Norden_die_cookie_eet_nom.png',
  link: 'https://cdn.magicpatterns.com/uploads/a7QwrfsRheFwXZaY2yk98u/Norden_die_cookie_eet_nom.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}];
const allItems: LibraryItem[] = [...blogPosts, ...clientCases, ...seoArticles, ...logos, ...nordenAssets];
const categories = [{
  id: 'all',
  label: 'Alle',
  types: ['blog', 'seo', 'case', 'logo', 'norden']
}, {
  id: 'blog',
  label: 'Blogartikelen',
  types: ['blog']
}, {
  id: 'seo',
  label: 'SEO Kennisbank',
  types: ['seo']
}, {
  id: 'case',
  label: 'Klant Cases',
  types: ['case']
}, {
  id: 'logo',
  label: "Logo's & Branding",
  types: ['logo']
}, {
  id: 'norden',
  label: 'Norden Mascotte',
  types: ['norden']
}];
export const BibliotheekPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const filteredItems = useMemo(() => {
    const activeTypes = categories.find(c => c.id === activeCategory)?.types || [];
    return allItems.filter(item => {
      const matchesType = activeTypes.includes(item.type);
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesSearch;
    });
  }, [activeCategory, searchQuery]);
  return <div className="min-h-screen bg-[#F7F8FA] flex flex-col" data-id="element-1188" data-mp="1p1pczbvzwez5">
      <SEO title="Bibliotheek met kennis, cases en merkbestanden | Noord Digital" description="Alle artikelen, klantcases en merkbestanden van Noord Digital op één plek. Kennis over online vindbaarheid, websites en vormgeving voor het lokale mkb." keywords="bibliotheek noord digital, kennisbank online marketing, klantcases, merkbestanden, logo download" canonical="https://noorddigital.nl/bibliotheek" jsonLd={[buildCollectionSchema({
      name: 'Bibliotheek van Noord Digital',
      description: 'Artikelen, klantcases en merkbestanden over online vindbaarheid, websites en vormgeving voor het lokale mkb.',
      path: '/bibliotheek',
      items: [...allBlogPosts.map(post => ({
        name: post.title,
        path: `/blog/${post.slug}`
      })), ...articles.map(article => ({
        name: article.title,
        path: `/seo/blog/${article.slug}`
      }))]
    }), buildBreadcrumbList([{
      name: 'Bibliotheek',
      path: '/bibliotheek'
    }])]} data-id="element-1189" data-mp="1k5i5henyavf3" />
      <Header data-id="element-1190" data-mp="2bbxffzw53dgc" />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#007AFF] to-[#1B2B44] pt-32 pb-20 relative overflow-hidden" data-id="element-1191" data-mp="2bame06q62hse">
        <div className="absolute inset-0 opacity-10" data-id="element-1192" data-mp="1wg7tu2uv92c5">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1507842217343-583bb7270b66?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center" data-id="element-1193" data-mp="21cey7r7m65rz"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10 text-center" data-id="element-1194" data-mp="1sdl8mkspafnx">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-id="element-1195" data-mp="1dooty4w524ec">
            Bibliotheek met kennis, cases en merkbestanden
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1196" data-mp="18nhhvtmyqj1v">
            Alle kennis, cases en merkbestanden van Noord Digital verzameld op
            één plek.
          </motion.p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] py-12 flex-grow" data-id="element-1197" data-mp="u12dtuwiz58k">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1198" data-mp="18xtf69prer0t">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-4" data-id="element-1199" data-mp="13z8de5uuzza9">
            <button onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)} className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between font-semibold text-[#111827] shadow-sm" data-id="element-1200" data-mp="13y6zltmgkyou">
              <span className="flex items-center gap-2" data-id="element-1201" data-mp="1rpcdi38u9gwi">
                <FilterIcon className="w-5 h-5 text-[#007AFF]" data-id="element-1202" data-mp="1rpvmuvkfawyx" />
                Filters & Categorieën
              </span>
              <span className="text-sm text-gray-500" data-id="element-1203" data-mp="w7tkt1kgw9t2">
                {categories.find(c => c.id === activeCategory)?.label}
              </span>
            </button>
          </div>

          {/* Sidebar */}
          <div className={`lg:w-64 flex-shrink-0 ${isMobileFiltersOpen ? 'block' : 'hidden lg:block'}`} data-id="element-1204" data-mp="nf9v4qm5lfkd">
            <div className="sticky top-24 bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-id="element-1205" data-mp="igoq8dvyi7pl">
              <h3 className="font-bold text-[#111827] mb-4 text-lg" data-id="element-1206" data-mp="1f77km04voerh">
                Categorieën
              </h3>
              <div className="space-y-2" data-id="element-1207" data-mp="1b4ubd1qx9ypy">
                {categories.map(category => <button key={category.id} onClick={() => {
                setActiveCategory(category.id);
                setIsMobileFiltersOpen(false);
              }} className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 font-medium ${activeCategory === category.id ? 'bg-[#007AFF] text-white shadow-md' : 'text-[#6B7280] hover:bg-gray-50 hover:text-[#111827]'}`} data-id="element-1208" data-mp="165r8nnyf04l5">
                    {category.label}
                    <span className={`float-right text-xs py-1 px-2 rounded-full ${activeCategory === category.id ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`} data-id="element-1209" data-mp="3bu9r6j5x9bo3">
                      {category.id === 'all' ? allItems.length : allItems.filter(item => category.types.includes(item.type)).length}
                    </span>
                  </button>)}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1" data-id="element-1210" data-mp="3ujgbgamd6bc0">
            {/* Search Bar */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 mb-8 flex items-center" data-id="element-1211" data-mp="3cdppypaox96">
              <div className="pl-4 pr-2" data-id="element-1212" data-mp="8d1cu9aekty4">
                <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-1213" data-mp="dbkys9ozy5qh" />
              </div>
              <input type="text" placeholder="Zoek in artikelen, cases of bestanden..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full py-3 px-2 outline-none text-[#111827] placeholder-gray-400 bg-transparent" data-id="element-1214" data-mp="d9i9jrvo4gmt" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="px-4 text-sm text-gray-400 hover:text-gray-600" data-id="element-1215" data-mp="2731l7bqqzl1x">
                  Wis
                </button>}
            </div>

            {/* Results Grid */}
            {filteredItems.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-1216" data-mp="2c1ct3gxhflhi">
                <AnimatePresence mode="popLayout" data-id="element-1217" data-mp="2c3fuex43511j">
                  {filteredItems.map(item => <motion.div layout initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} transition={{
                duration: 0.2
              }} key={item.id} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col group" data-id="element-1218" data-mp="2pzjjyd40kl7o">
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden bg-gray-100" data-id="element-1219" data-mp="3nekh4odz5bhv">
                        <img src={item.image} alt={item.title} loading="lazy" className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${item.type === 'logo' || item.type === 'norden' || item.imageContain ? 'object-contain p-4' : ''}`} data-id="element-1220" data-mp="3nchv0ffy02if" />
                        <div className="absolute top-4 left-4" data-id="element-1221" data-mp="3jaw964lfkdw3">
                          <span className={`text-xs font-semibold px-3 py-1 rounded-full text-white ${item.type === 'blog' ? 'bg-[#007AFF]' : item.type === 'seo' ? 'bg-[#1B2B44]' : item.type === 'case' ? 'bg-emerald-500' : 'bg-purple-500'}`} data-id="element-1222" data-mp="34nkvnx5oujtc">
                            {item.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-grow" data-id="element-1223" data-mp="2lqa0ann6gaj6">
                        <h3 className="font-bold text-[#111827] mb-3 text-lg line-clamp-2 group-hover:text-[#007AFF] transition-colors" data-id="element-1224" data-mp="2qola4ydh2l1e">
                          {item.title}
                        </h3>
                        <p className="text-[#6B7280] mb-6 line-clamp-3 text-sm flex-grow" data-id="element-1225" data-mp="3ozctdg93syej">
                          {item.description}
                        </p>

                        {/* Websitelink boven de knop, zodat de knoppen in elke rij op één lijn blijven */}
                        {item.type === 'case' && item.website && <div className="-mt-3 mb-4" data-id="element-1226" data-mp="3ac1dx39swu95">
                            <CaseWebsiteLink url={item.website} clientName={item.title} className="max-w-full" data-id="element-1227" data-mp="35bvjjfc5ctnd" />
                          </div>}

                        <a href={item.link} className={`inline-flex items-center justify-center w-full py-3 px-4 rounded-lg font-medium transition-all duration-300 ${item.type === 'logo' || item.type === 'norden' ? 'bg-purple-50 text-purple-600 hover:bg-purple-100' : 'bg-blue-50 text-[#007AFF] hover:bg-[#007AFF] hover:text-white'}`} data-id="element-1228" data-mp="31bbm0annvo0p">
                          {item.type === 'logo' || item.type === 'norden' ? <>
                              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1229" data-mp="122ttqsy1traz" />
                              {item.actionText}
                            </> : <>
                              {item.actionText}
                              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1230" data-mp="1bty9huvjtg6v" />
                            </>}
                        </a>
                      </div>
                    </motion.div>)}
                </AnimatePresence>
              </div> : <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm" data-id="element-1231" data-mp="o3kf3xphr594">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1232" data-mp="t2ooaocf12zg">
                  <SearchIcon className="w-8 h-8 text-gray-400" data-id="element-1233" data-mp="x4s1piinahnd" />
                </div>
                <h3 className="text-xl font-bold text-[#111827] mb-2" data-id="element-1234" data-mp="24i3vsm293d8t">
                  Geen resultaten gevonden
                </h3>
                <p className="text-[#6B7280]" data-id="element-1235" data-mp="28ie2y21s49p9">
                  We konden geen items vinden die overeenkomen met "
                  {searchQuery}". Probeer een andere zoekterm of categorie.
                </p>
                <button onClick={() => {
              setSearchQuery('');
              setActiveCategory('all');
            }} className="mt-6 text-[#007AFF] font-medium hover:underline" data-id="element-1236" data-mp="4eszfxsun1su">
                  Wis alle filters
                </button>
              </div>}
          </div>
        </div>
      </div>

      <Footer data-id="element-1237" data-mp="3lwlyj9dufpey" />
    </div>;
};